import { Database, FileText, Search, SearchX } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import { type SearchScope, useRoomSearch } from "@/api/queries/workplace"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { formatNumber } from "@/lib/format"
import { RoomDataSection } from "./room-data-section"

const SCOPES: SearchScope[] = ["all", "data", "files"]

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = React.useState(value)
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

function Hit({
  icon: Icon,
  title,
  meta,
  snippet,
}: {
  icon: typeof FileText
  title: string
  meta: string
  snippet: string
}) {
  return (
    <li className="rounded-lg border px-3 py-2" data-testid="room-search-hit">
      <div className="flex items-center gap-1.5">
        <Icon className="text-muted-foreground size-3.5 shrink-0" />
        <span className="min-w-0 truncate font-mono text-xs font-medium">{title}</span>
        <span className="text-muted-foreground ml-auto shrink-0 text-[0.6875rem]">{meta}</span>
      </div>
      {snippet && (
        <p className="text-muted-foreground mt-1 line-clamp-3 text-xs break-words whitespace-pre-wrap">
          {snippet}
        </p>
      )}
    </li>
  )
}

/** Fuzzy search over the room's shared data and files, plus the room data editor. */
export function RoomSearchPanel({ room, onOpenFiles }: { room: RoomDetail; onOpenFiles: () => void }) {
  const { t } = useTranslation()
  const [q, setQ] = React.useState("")
  const [scope, setScope] = React.useState<SearchScope>("all")
  const query = useDebounced(q, 250)
  const search = useRoomSearch(room.id, query, scope)
  const hits = [...(search.data?.data_hits ?? []), ...(search.data?.file_hits ?? [])]

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("workplace.room.search.placeholder")}
            aria-label={t("workplace.room.search.placeholder")}
            className="h-8 pl-8 text-[0.8125rem]"
            data-testid="room-search-input"
          />
        </div>
        <ToggleGroup
          value={[scope]}
          onValueChange={(v: unknown[]) => v[0] && setScope(v[0] as SearchScope)}
          variant="outline"
          size="sm"
          spacing={0}
          aria-label={t("workplace.room.search.scope")}
        >
          {SCOPES.map((s) => (
            <ToggleGroupItem key={s} value={s} className="px-2.5 text-xs">
              {t(`workplace.room.search.scopes.${s}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {query.trim() ? (
        search.isPending ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : search.isError ? (
          <ErrorState variant="plain" error={search.error} onRetry={() => void search.refetch()} />
        ) : hits.length === 0 ? (
          <EmptyState
            variant="plain"
            size="sm"
            icon={SearchX}
            title={t("workplace.room.search.noResults", { query })}
          />
        ) : (
          <ul className="space-y-1.5">
            {search.data?.data_hits?.map((h) => (
              <Hit
                key={`d-${h.key}`}
                icon={Database}
                title={h.key}
                meta={t("workplace.room.search.dataMeta", { chars: formatNumber(h.size) })}
                snippet={h.snippet}
              />
            ))}
            {search.data?.file_hits?.map((h) => (
              <Hit
                key={`f-${h.id}`}
                icon={FileText}
                title={h.filename}
                meta={t("workplace.room.search.fileMeta")}
                snippet={h.snippet}
              />
            ))}
          </ul>
        )
      ) : (
        <RoomDataSection room={room} onOpenFiles={onOpenFiles} />
      )}
    </div>
  )
}
